/**
 * Transit Calendar Service
 * Handles all transit calendar API calls to AWS Lambda functions
 * Integrated with AWS Cognito, API Gateway, and DynamoDB
 */

// Production API Gateway endpoint
const API_BASE_URL = 'https://pxq7fyhjsf.execute-api.eu-west-1.amazonaws.com/prod';

/**
 * Register a new user with birth data and natal calculations
 * @param {Object} params - Registration parameters
 * @param {string} params.name - User's full name
 * @param {string} params.email - User's email address
 * @param {string} params.password - User's password
 * @param {string} params.birthDate - ISO date string
 * @param {string} params.birthTime - Birth time string
 * @param {string} params.birthLocation - Birth location (city, country)
 * @param {number} params.birthLat - Birth latitude
 * @param {number} params.birthLon - Birth longitude
 * @param {string} params.timezone - Timezone string
 * @param {Object} params.natalPositions - Calculated natal planet positions
 * @param {string} params.source - Where this person came from (utils/trafficSource)
 * @returns {Promise<Object>} Response with token and user data
 */
export const registerUser = async ({
  name, email, password,
  birthDate, birthTime, birthLocation,
  birthLat, birthLon, timezone,
  natalPositions, source
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name, email, password,
        birthDate, birthTime, birthLocation,
        birthLat, birthLon, timezone,
        natalPositions, source
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to register user');
    }

    return data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

/**
 * Log in an existing user
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} Response with token and user data
 */
export const loginUser = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to log in');
    }

    return data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

/**
 * Get user's natal data and permissions
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} User's natal data and permissions
 */
export const getUserNatalData = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/natal`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get user data');
    }

    return data;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

/**
 * Get natal data for the currently logged in user
 * Uses token from localStorage
 * @param {string} profileId - Optional profile ID to get specific profile
 * @returns {Promise<Object>} User's natal data and permissions
 */
export const getNatalData = async (profileId = null) => {
  const token = localStorage.getItem('transit-calendar-token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const url = profileId
    ? `${API_BASE_URL}/natal?profile_id=${profileId}`
    : `${API_BASE_URL}/natal`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get natal data');
    }

    return data;
  } catch (error) {
    console.error('Error getting natal data:', error);
    throw error;
  }
};

/**
 * Add a new profile for the current user
 * @param {Object} params - Profile parameters
 * @param {string} params.profileName - Name for this profile
 * @param {string} params.birthDate - ISO date string
 * @param {string} params.birthTime - Birth time string
 * @param {string} params.birthLocation - Birth location (city, country)
 * @param {number} params.birthLat - Birth latitude
 * @param {number} params.birthLon - Birth longitude
 * @param {string} params.timezone - Timezone string
 * @param {Object} params.natalPositions - Calculated natal planet positions
 * @returns {Promise<Object>} Response with new profile data
 */
export const addProfile = async ({
  profileName, birthDate, birthTime, birthLocation,
  birthLat, birthLon, timezone, natalPositions
}) => {
  const token = localStorage.getItem('transit-calendar-token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/profiles/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        profileName, birthDate, birthTime, birthLocation,
        birthLat, birthLon, timezone, natalPositions
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to add profile');
    }

    return data;
  } catch (error) {
    console.error('Error adding profile:', error);
    throw error;
  }
};

/**
 * Admin: Get list of all users
 * @param {string} adminToken - Admin JWT token
 * @returns {Promise<Object>} List of users
 */
export const adminListUsers = async (adminToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/transit-calendar/admin/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to list users');
    }

    return data;
  } catch (error) {
    console.error('Error listing users:', error);
    throw error;
  }
};

/**
 * Admin: Get specific user's natal data
 * @param {string} adminToken - Admin JWT token
 * @param {string} userId - User ID to fetch
 * @returns {Promise<Object>} User's natal data
 */
export const adminGetUserNatal = async (adminToken, userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/transit-calendar/admin/user-natal/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get user natal data');
    }

    return data;
  } catch (error) {
    console.error('Error getting user natal data:', error);
    throw error;
  }
};

/**
 * Admin: Update user permissions
 * @param {string} adminToken - Admin JWT token
 * @param {string} userId - User ID to update
 * @param {Object} permissions - Permission flags
 * @param {boolean} permissions.weekly_view - Allow weekly calendar view
 * @param {boolean} permissions.monthly_view - Allow monthly calendar view
 * @param {boolean} permissions.annual_view - Allow annual calendar view
 * @returns {Promise<Object>} Updated user data
 */
export const adminUpdatePermissions = async (adminToken, userId, permissions) => {
  try {
    const response = await fetch(`${API_BASE_URL}/transit-calendar/admin/permissions/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ permissions })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update permissions');
    }

    return data;
  } catch (error) {
    console.error('Error updating permissions:', error);
    throw error;
  }
};

export default {
  registerUser,
  loginUser,
  getUserNatalData,
  getNatalData,
  addProfile,
  adminListUsers,
  adminGetUserNatal,
  adminUpdatePermissions
};
